import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { RequiredTag } from '../Input/RequiredTag';

const CategorySelect = ({
                            selectedCreativeCategories,
                            service,
                            dispatch,
                            validator,
                            onChange,
                        }) => {
    const { t } = useTranslation();
    const [categOptions, setCategOptions] = useState([]);

    const errors = validator.errors.selectedCreativeCategories;

    const putValueAndLabel = (category) => {
        if (!category || typeof category !== 'object') {
            return { uniqueId: '', name: '' };
        }
        return {
            uniqueId: category.uniqueId ?? '',
            name: category.name ?? '',
        };
    };

    const fetchData = useCallback(async () => {
        try {
            const fullLang = window.navigator.language || 'en';
            const lang = fullLang.split('-')[0];

            const response = await service.getCreativeCategories(null, lang);
            const rawCategories = Array.isArray(response?.message)
                ? response.message
                : Array.isArray(response)
                    ? response
                    : [];

            const allCategories = rawCategories.map(putValueAndLabel);
            setCategOptions(allCategories);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('getCreativeCategories failed', error);
            setCategOptions([]);
        }
    }, [service]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleOnChange = (newCategoriesSelected) => {
        const uniqueIds = newCategoriesSelected
            ? newCategoriesSelected.map((category) => Number(category.uniqueId))
            : [];

        onChange(uniqueIds);
    };

    const selectedOptions = useMemo(() => {
        if (!categOptions || categOptions.length === 0) return [];

        const idFromSelected = (s) =>
            s && typeof s === 'object' && s.uniqueId != null
                ? String(s.uniqueId)
                : String(s);

        const selectedIdSet = new Set(
            (selectedCreativeCategories || []).map(idFromSelected)
        );

        return categOptions.filter((opt) =>
            selectedIdSet.has(String(opt.uniqueId))
        );
    }, [categOptions, selectedCreativeCategories]);

    return (
        <div className="module rounded ml-1 mb-4">
            <div className="title text-xl font-extralight">
                {t(
                    'sections.campaigns.new-campaign.wizard-basics.advertising.title'
                )}
            </div>
            <label className="mb-1 block text-sm font-medium text-gray-700 mt-2">
                {t(
                    'sections.campaigns.new-campaign.wizard-basics.advertising.select-categories'
                )}{' '}
                <RequiredTag alert={errors} />
            </label>
            <div className="lg:w-3/6 2xl:w-2/6">
                <Select
                    value={selectedOptions}
                    options={categOptions}
                    onChange={handleOnChange}
                    isMulti
                    isOptionDisabled={() =>
                        selectedOptions && selectedOptions.length >= 1
                    }
                    className="basic-multi-select"
                    classNamePrefix="select"
                    styles={{
                        control: (base, state) => ({
                            ...base,
                            borderColor: errors ? '#ef4444' : base.borderColor, // red-500
                            boxShadow: errors
                                ? '0 0 0 1px #ef4444'
                                : base.boxShadow,
                            '&:hover': {
                                borderColor: errors
                                    ? '#ef4444'
                                    : base.borderColor,
                            },
                        }),
                    }}
                    getOptionValue={(option) => option.uniqueId}
                    getOptionLabel={(option) => option.name}
                    inputId="ad-category"
                    onBlur={() => {
                        validator.touchField('selectedCreativeCategories');
                        validator.validateField('selectedCreativeCategories');
                    }}
                />
                {validator.shouldShowError('selectedCreativeCategories') && (
                    <p className="mt-1 text-sm text-red-500">{errors}</p>
                )}
            </div>
        </div>
    );
};

CategorySelect.propTypes = {
    selectedCreativeCategories: PropTypes.array,
    service: PropTypes.object.isRequired,
    dispatch: PropTypes.func,
};

export default CategorySelect;
